import React, { useState, useEffect } from 'react';
import styles from './WorldCupPage.module.css';
import WorldCupHeader, { WorldCupTabs } from './WorldCupHeader';
import WorldCupGroups from './WorldCupGroups';
import WorldCupBrackets from './WorldCupBrackets';
import WorldCupSquad from './WorldCupSquad';
import WorldCupFixtures from './WorldCupFixtures';
import WorldCupGoldenBoot from './WorldCupGoldenBoot';
import WorldCupArticles from './WorldCupArticles';
import IdentityGate from '../custom-widgets/IdentityGate';
import { KLAVIYO_PUBLIC_KEY, KLAVIYO_LIST_ID } from '../../../server/services/custom-pages/constants';
import { syncFromKlaviyo, store, getCurrentPublisherName, subscribeEntityToKlaviyo } from '../../../server/services/custom-pages/auth';

export default function WorldCupPage({ initialIsMember = false, wcData = null, initialArticles = [] }) {
  const [activeTab, setActiveTab] = useState('GROUP TABLES');
  const [isMember, setIsMember] = useState(initialIsMember || (typeof document !== 'undefined' && document.cookie.includes('klaviyo_member=true')));
  const [isRevealing, setIsRevealing] = useState(false);
  const [email, setEmail] = useState('');
  const [emailError, setEmailError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    syncFromKlaviyo().then(res => {
      if (res.isSignedIn && res.email) {
        setEmail(res.email);
        store.set("fi_was_signed_in", "true");
        
        // If the user has a Klaviyo profile and is a member of the current publisher, auto-unlock
        const currentPub = getCurrentPublisherName();
        if (res.publishers && res.publishers.includes(currentPub)) {
          document.cookie = 'klaviyo_member=true; path=/; max-age=' + (60 * 60 * 24 * 365);
          setIsMember(true);
        }
      }
    }).catch(err => {
      console.error('[WorldCupPage] sync error:', err);
    });
  }, [initialIsMember]);

  const handleUnlock = async (e) => {
    e.preventDefault();
    if (!email || !email.includes('@')) {
      setEmailError('Please enter a valid email address.');
      return;
    }
    setIsSubmitting(true);
    setEmailError("");
    store.set("user_email", email);
    document.cookie = 'klaviyo_member=true; path=/; max-age=' + (60 * 60 * 24 * 365);
    setIsRevealing(true);
    setTimeout(() => {
      setIsMember(true);
      setIsRevealing(false);
      setIsSubmitting(false);
    }, 800);

    subscribeEntityToKlaviyo(email, null).catch(err => {
      console.warn("[WorldCupPage] Klaviyo background subscription:", err);
    });
  };

  return (
    <div className={styles.pageContainer}>
      <WorldCupHeader />

      {/* Main content wrapper */}
      <div className={styles.contentWrapper}>
        {/* Gated tab content */}
        <div style={{ position: 'relative' }}>
          <IdentityGate
            isMember={isMember}
            isRevealing={isRevealing}
            email={email}
            setEmail={setEmail}
            emailError={emailError}
            setEmailError={setEmailError}
            isSubmitting={isSubmitting}
            handleUnlock={handleUnlock}
          />
          <div className={`${styles.contentReveal} ${!isMember ? styles.blurredContentLocked : ''}`}>
            <WorldCupTabs activeTab={activeTab} setActiveTab={setActiveTab} />
            <div className={styles.tabContentActive}>
              {activeTab === 'GROUP TABLES' && <WorldCupGroups groups={wcData?.groups} />}
              {activeTab === 'KNOCKOUT TREE' && <WorldCupBrackets events={wcData?.knockout} />}
              {activeTab === 'SQUAD LIST' && <WorldCupSquad teams={wcData?.teams} squads={wcData?.squads} photoMap={wcData?.photoMap} />}
              {activeTab === 'FIXTURES' && <WorldCupFixtures matches={wcData?.fixtures} />}
              {activeTab === 'GOLDEN BOOT' && <WorldCupGoldenBoot players={wcData?.goldenBoot} photoMap={wcData?.photoMap} />}
            </div>
          </div>
        </div>

        {/* Second decorative blob */}
        <div className={styles.blob2Container} aria-hidden="true">
          <svg
            className={styles.blob2}
            viewBox="0 0 1638 1159"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <g filter="url(#blob2)">
              <path
                d="M604.732 118.921C386.682 -139.673 171.475 83.0056 91.1273 226.669C26.2314 279.924 -67.9604 469.287 74.4398 800.704C252.44 1214.98 829.087 1185.25 1246.27 1122.09C1663.46 1058.93 1771 629.794 1453.94 449.595C1136.88 269.397 1348.25 886.159 766.045 845.289C183.836 804.42 877.295 442.164 604.732 118.921Z"
                fill="url(#blobGrad2)"
              />
            </g>
            <defs>
              <filter id="blob2" x="-399" y="-399" width="2436" height="1957" filterUnits="userSpaceOnUse" colorInterpolationFilters="sRGB">
                <feFlood floodOpacity="0" result="BackgroundImageFix" />
                <feBlend mode="normal" in="SourceGraphic" in2="BackgroundImageFix" result="shape" />
                <feGaussianBlur stdDeviation="199.5" result="effect" />
              </filter>
              <linearGradient id="blobGrad2" x1="819" y1="1159" x2="819" y2="0" gradientUnits="userSpaceOnUse">
                <stop stopColor="#0D0D0D" />
                <stop offset="1" stopColor="#D4AF37" />
              </linearGradient>
            </defs>
          </svg>
        </div>

        {/* Articles Section at bottom */}
        <WorldCupArticles initialArticles={initialArticles} />
      </div>
    </div>
  );
}
